import { COLORS } from '../../game/data/colors';
import { IMAGES } from '../../game/data/images';
import { observer } from 'mobx-react-lite';
import { GreenButton, YellowButton } from './Reusable/GreenButton';
import { DATA } from '../../game/data';
import { Airport } from '../../game/airport';
import { formatNumber, uuid } from '../../game/helpers';
import { TripData } from '../../game/data.type';
import { AIRPLANE_DATA } from '../../game/data/airplaneData';
import { TextButton } from './Reusable/TextButton';
import { text } from 'stream/consumers';
import { ManagerProgressBar } from './Reusable/ManagerProgressBar';
import styled from 'styled-components';
import { AirportSlug } from '../../game/data/airportData';

export const AirportPopup: React.FC = observer(() => {
  if (!DATA.planTripPopup) {
    return null;
  }

  const airport = DATA.airportBySlug(DATA.planTripPopup);
  const destinationAirports = DATA.airports.filter((a) => a.isAvailable && airport.otherSlugs.includes(a.slug));
  const sortedAirplanes = airport.getAirplanes().sort((a, b) => b.getUpgradeValue('capacity') - a.getUpgradeValue('capacity'));

  const tripMap: { [k: string]: { trips: Partial<{ [key in AirportSlug]: TripData }>; mostExpensive?: AirportSlug } } = {};

  sortedAirplanes.forEach((airplane) => {
    tripMap[airplane.id] = { trips: {} };
    destinationAirports.forEach((toAirport) => {
      tripMap[airplane.id].trips[toAirport.slug] = airplane.computeTripData(airport, toAirport);
    });

    const mostExpensiveAirport = destinationAirports.reduce((prev: Airport, curr: Airport) => {
      const prevTripData = tripMap[airplane.id].trips[prev.slug];
      const currTripData = tripMap[airplane.id].trips[curr.slug];
      return prevTripData && currTripData && currTripData.profit > prevTripData.profit ? curr : prev;
    }, destinationAirports[0]);

    tripMap[airplane.id].mostExpensive = mostExpensiveAirport?.slug;
  });

  const renderAirportRow = (destinationAirport: Airport) => {
    let textColor = COLORS.white;
    if (destinationAirport.isPopulationIncreaseEvent) {
      textColor = COLORS.green_500;
    }

    return (
      <tr key={destinationAirport.slug}>
        <td style={{ color: textColor }}>{destinationAirport.name} </td>
        <td>
          <img src={IMAGES.ICONS.person} alt="person" style={{ height: '1.2rem', marginRight: 2 }} />
          {airport.passengers[destinationAirport.slug] || 0}
        </td>
        {sortedAirplanes.map((airplane) => {
          const tripData = tripMap[airplane.id].trips[destinationAirport.slug]!;

          const beginTrip = () => {
            if (DATA.tutorialStep === 'step2') {
              DATA.setTutorialStep('step3');
            }
            airplane.beginTrip(tripData);
            if (airport.getAirplanes().length === 0) {
              DATA.showPlanTripPopup(null);
            }
          };

          const buttonContent = (
            <>
              <img src={IMAGES.ICONS.person} alt="person" style={{ height: '1rem' }} />
              {tripData.passengers}
              <div style={{ flex: 1, textAlign: 'right', marginLeft: 6 }}>${formatNumber(tripData.profit)}</div>
            </>
          );
          const buttonStyle = {
            fontSize: '1rem',
            minWidth: '6.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4px 8px',
            width: '100%',
          };

          return (
            <td key={airplane.id}>
              {destinationAirport.slug === tripMap[airplane.id].mostExpensive ? (
                <YellowButton style={buttonStyle} onClick={beginTrip}>
                  {buttonContent}
                </YellowButton>
              ) : (
                <GreenButton style={buttonStyle} onClick={beginTrip}>
                  {buttonContent}
                </GreenButton>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  const manger = DATA.managers.find((m) => m.airportSlug === airport.slug);

  return (
    <div
      style={{
        position: 'absolute',
        inset: '0',
        backgroundColor: COLORS.backdrop,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        flexDirection: 'column',
      }}
      onClick={() => DATA.showPlanTripPopup(null)}
    >
      <div style={{ color: 'white', marginBottom: 8 }}>Click anywhere to close</div>
      <div
        style={{
          backgroundColor: COLORS.zinc_800,
          padding: '16px',
          borderRadius: '16px 16px 0px 0px',
          minWidth: 500,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          maxWidth: '80%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${COLORS.zinc_600}`,
            marginBottom: 8,
            padding: '0px 16px 8px 16px',
            marginLeft: -16,
            marginRight: -16,
          }}
        >
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <h2>{airport.name}</h2>
          </div>
          <div>
            <TextButton
              onClick={() => {
                DATA.setOpenSidebar('airports', { highlightedAirportSlug: airport.slug });
              }}
            >
              Airport <img src={IMAGES.ICONS.link} alt="link" style={{ height: '1rem' }} />
            </TextButton>
            <TextButton
              onClick={() => {
                DATA.setOpenSidebar('managers', { highlightedAirportSlug: airport.slug });
              }}
            >
              Manager <img src={IMAGES.ICONS.link} alt="link" style={{ height: '1rem' }} />
            </TextButton>
            <TextButton
              onClick={() => {
                DATA.setOpenSidebar('lounges', { highlightedAirportSlug: airport.slug });
              }}
            >
              Lounge <img src={IMAGES.ICONS.link} alt="link" style={{ height: '1rem' }} />
            </TextButton>
          </div>
        </div>
        <div style={{ overflowY: 'auto' }}>
          {sortedAirplanes.length === 0 && (
            <div
              style={{
                height: 40,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: COLORS.zinc_400,
                backgroundColor: COLORS.zinc_700,
                borderRadius: 4,
              }}
            >
              No airplanes in this airport
            </div>
          )}
          <TripTable style={{ marginBottom: 8, textAlign: 'center' }}>
            <tbody>
              {sortedAirplanes.length > 0 && (
                <tr>
                  <td></td>
                  <td style={{ minWidth: '5rem' }}>{/* passengers */}</td>
                  {sortedAirplanes.map((airplane) => (
                    <td key={airplane.id}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <img
                          src={IMAGES.AIRPLANES[airplane.color]}
                          alt={'airplane'}
                          style={{
                            width: '1.2rem',
                            marginRight: 4,
                          }}
                        />
                        {airplane.name}
                      </div>
                    </td>
                  ))}
                </tr>
              )}
              {destinationAirports.map((airport) => renderAirportRow(airport))}
            </tbody>
          </TripTable>
        </div>

        {DATA.tutorialStep === 'step2' && (
          <div
            style={{
              color: COLORS.zinc_400,
              backgroundColor: COLORS.zinc_700,
              padding: '8px 16px',
              borderRadius: 4,
            }}
          >
            Click the buttons to start a trip. <b style={{ color: COLORS.yellow_500 }}>YELLOW</b> buttons show the most profitable trip.
          </div>
        )}

        {manger && (
          <div className="tooltip-text" style={{ textAlign: 'center' }}>
            While this popup is open the manager ignores these airplanes
          </div>
        )}
      </div>
    </div>
  );
});

const TripTable = styled('table')({
  margin: 0,
  padding: 0,
  borderCollapse: 'collapse',
  '& td': {
    padding: '0px 8px', // Remove padding
    height: 40,
  },
  '& tr': {
    margin: 0, // Remove margins from rows if any
    padding: 0, // Remove padding from rows if any
  },
  '& th': {
    margin: 0, // Remove margins from headers if any
    padding: 0, // Remove padding from headers if any
  },
});
